
import { useState, useEffect } from "react";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";

// Cookie consent preferences type
type ConsentPreferences = {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
  version: string;
};

// Default preferences with necessary cookies always true
const defaultPreferences: ConsentPreferences = {
  necessary: true,  // Always required
  functional: false,
  analytics: false,
  marketing: false,
  timestamp: Date.now(),
  version: "1.0"  // Update this when making significant privacy policy changes
};

export const CookieConsent = () => {
  const [open, setOpen] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<ConsentPreferences>(defaultPreferences);

  // Check for existing consent on component mount
  useEffect(() => {
    const storedConsent = localStorage.getItem("cookie-consent");
    if (!storedConsent) {
      // No consent found, show the banner after a short delay
      const timer = setTimeout(() => setOpen(true), 1000);
      return () => clearTimeout(timer);
    } else {
      try {
        // Parse stored preferences
        const storedPreferences = JSON.parse(storedConsent);
        setPreferences(storedPreferences);
      } catch (e) {
        console.error("Failed to parse stored consent:", e);
        setOpen(true);
      }
    }
  }, []);

  // Save consent preferences
  const saveConsent = (prefs: ConsentPreferences) => {
    const consentData = {
      ...prefs,
      timestamp: Date.now()
    };
    
    localStorage.setItem("cookie-consent", JSON.stringify(consentData));
    setPreferences(consentData);
    
    // Here you would also implement actual cookie/tracking enabling or disabling
    // based on the user's choices
    console.log("Consent preferences saved:", consentData);
    
    // Set cookies based on preferences
    if (consentData.analytics) {
      // Enable analytics cookies/tracking
      console.log("Analytics enabled");
    }
    
    if (consentData.marketing) {
      // Enable marketing cookies/tracking
      console.log("Marketing enabled");
    }
    
    if (consentData.functional) {
      // Enable functional cookies/tracking
      console.log("Functional cookies enabled");
    }
  };

  // Accept all cookies
  const acceptAll = () => {
    const allConsent = {
      ...preferences,
      functional: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now()
    };
    saveConsent(allConsent);
    setOpen(false);
  };

  // Accept only necessary cookies
  const acceptNecessary = () => {
    saveConsent({
      ...defaultPreferences,
      timestamp: Date.now()
    });
    setOpen(false);
  };

  // Save preferences
  const savePreferences = () => {
    saveConsent(preferences);
    setOpen(false);
    setShowPreferences(false);
  };

  // Re-open the cookie consent dialog
  const openConsentManager = () => {
    setShowPreferences(true);
    setOpen(true);
  };

  return (
    <>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="max-w-md sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Cookie Preferences</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-muted-foreground">
              We use cookies to enhance your browsing experience, serve personalized ads or content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {showPreferences ? (
            // Detailed preferences view
            <div className="space-y-4 my-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Necessary Cookies</h4>
                  <p className="text-xs text-muted-foreground">Required for the website to function. Cannot be disabled.</p>
                </div>
                <Switch checked={true} />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Functional Cookies</h4>
                  <p className="text-xs text-muted-foreground">Enable personalized features and remember your preferences.</p>
                </div>
                <Switch 
                  checked={preferences.functional} 
                  onCheckedChange={(checked) => setPreferences({...preferences, functional: checked})} 
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Analytics Cookies</h4>
                  <p className="text-xs text-muted-foreground">Help us understand how you interact with the website.</p>
                </div>
                <Switch 
                  checked={preferences.analytics} 
                  onCheckedChange={(checked) => setPreferences({...preferences, analytics: checked})} 
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Marketing Cookies</h4>
                  <p className="text-xs text-muted-foreground">Used to deliver relevant ads and track their performance.</p>
                </div>
                <Switch 
                  checked={preferences.marketing} 
                  onCheckedChange={(checked) => setPreferences({...preferences, marketing: checked})} 
                />
              </div>
            </div>
          ) : (
            // Simple consent view
            <div className="py-2">
              <p className="text-sm">
                Read our <Link to="/privacy-policy" className="text-primary hover:underline" onClick={() => setOpen(false)}>Privacy Policy</Link> and <Link to="/terms-of-service" className="text-primary hover:underline" onClick={() => setOpen(false)}>Terms of Service</Link> for more information.
              </p>
            </div>
          )}

          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            {showPreferences ? (
              <>
                <AlertDialogCancel onClick={() => setShowPreferences(false)}>
                  Back
                </AlertDialogCancel>
                <AlertDialogAction onClick={savePreferences}>
                  Save Preferences
                </AlertDialogAction>
              </>
            ) : (
              <>
                <div className="flex flex-1 flex-col sm:flex-row gap-2">
                  <Button variant="outline" className="sm:flex-1" onClick={acceptNecessary}>
                    Necessary Only
                  </Button>
                  <Button variant="outline" className="sm:flex-1" onClick={() => setShowPreferences(true)}>
                    Preferences
                  </Button>
                </div>
                <AlertDialogAction onClick={acceptAll} className="sm:flex-1">
                  Accept All
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Floating button to manage consent later */}
      <div className="fixed bottom-4 left-4 z-50">
        <Button 
          onClick={openConsentManager}
          className="text-xs bg-background/80 backdrop-blur-sm shadow-md"
          size="sm"
          variant="outline"
        >
          Cookie Settings
        </Button>
      </div>
    </>
  );
};

export default CookieConsent;
